// FILE: src/pages/NursingHomeDashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import NursingHomeResidentDrawer from "./NursingHomeResidentDrawer";
import NursingHomeStaff from "./NursingHomeStaff";

const FN_BASE = "https://us-central1-lastwishbox-business.cloudfunctions.net";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#fff",
  border: "#e8ebf3",
};

const CONSENT_LABELS = {
  funeralPlanning: "Funeral planning",
  organDonation: "Organ donation",
  advanceHealthcareDirective: "Advance healthcare directive",
  personalMessages: "Personal messages",
  documents: "Documents",
  contacts: "Contacts",
};

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: B.white,
      border: `1px solid ${B.border}`,
      borderRadius: 16,
      padding: "24px 20px",
      boxShadow: "0 2px 8px rgba(0,0,0,.04)",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}>
      <div style={{ fontSize: 36, fontWeight: 900, color: color || B.navy }}>{value}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: B.redDark }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: B.steel }}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ active }) {
  const s = active
    ? { bg: "#ecfdf5", color: "#065f46", label: "Active" }
    : { bg: "#fffbeb", color: "#92400e", label: "Not activated" };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 12, fontWeight: 700,
      padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function ConsentBadge({ consent }) {
  if (!consent) {
    return (
      <span style={{ background: "#fef2f2", color: "#991b1b", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
        Not recorded
      </span>
    );
  }
  const granted = Object.keys(CONSENT_LABELS).filter((k) => consent[k] === true).length;
  const byRep = consent.consentGivenBy && consent.consentGivenBy !== "resident";
  return (
    <span
      title={byRep ? `Consent given by ${consent.consentGivenBy.replace(/_/g, " ")}: ${consent.representativeName || ""}` : "Consent given by resident"}
      style={{ background: "#ecfdf5", color: "#065f46", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}
    >
      {granted}/6{byRep ? " (rep)" : ""}
    </span>
  );
}

function WishesBadge({ pct, gated }) {
  if (gated) {
    return (
      <span style={{ background: "#f9fafb", color: "#6b7280", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
        Not shared
      </span>
    );
  }
  const color = pct === 100 ? "#065f46" : pct > 0 ? "#92400e" : "#6b7280";
  const bg = pct === 100 ? "#ecfdf5" : pct > 0 ? "#fffbeb" : "#f9fafb";
  const label = pct === 100 ? "Complete" : pct > 0 ? `${pct}% done` : "Not started";
  return (
    <span style={{ background: bg, color, fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function fmtDate(ms) {
  if (!ms) return "\u2014";
  try { return new Date(ms).toLocaleDateString("en-IE"); } catch { return "\u2014"; }
}

export default function NursingHomeDashboard({ tenantId, tenant, isOwner }) {
  // Fall back to reading the caller's own membership when the parent does not
  // pass a role, so the Staff tab is never shown to a non-owner.
  const [resolvedIsOwner, setResolvedIsOwner] = useState(isOwner === true);

  useEffect(() => {
    if (typeof isOwner === "boolean") { setResolvedIsOwner(isOwner); return; }
    let ignore = false;
    (async () => {
      try {
        const u = getAuth().currentUser;
        if (!u || !tenantId) return;
        const snap = await getDoc(doc(db, "tenants", tenantId, "members", u.uid));
        if (!ignore) setResolvedIsOwner(snap.exists() && (snap.data() || {}).role === "owner");
      } catch {
        if (!ignore) setResolvedIsOwner(false);
      }
    })();
    return () => { ignore = true; };
  }, [isOwner, tenantId]);

  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [warning, setWarning] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [openResident, setOpenResident] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setErr("");
    setWarning("");
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const token = await user.getIdToken();

      const r = await fetch(`${FN_BASE}/nhListResidents?tenantId=${encodeURIComponent(tenantId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);

      setResidents(Array.isArray(data.residents) ? data.residents : []);
      if (data.warning) setWarning(data.warning);
    } catch (e) {
      console.error("nhListResidents failed", e);
      setErr(String(e.message || e));
      setResidents([]);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Consent-aware view of each resident.
  // A section is only surfaced to the nursing home if the resident consented to share it.
  const view = residents.map((r) => {
    const c = r.nhConsent || null;
    const canSeeFuneral = !!(c && c.funeralPlanning);
    const canSeeOrgan = !!(c && c.organDonation);
    const canSeeContacts = !!(c && c.contacts);

    const visibleSections = [canSeeFuneral, canSeeOrgan].filter(Boolean).length;
    const visibleComplete =
      (canSeeFuneral && r.hasFuneralPlan ? 1 : 0) +
      (canSeeOrgan && r.hasOrganDonation ? 1 : 0);

    const pct = visibleSections > 0 ? Math.round((visibleComplete / visibleSections) * 100) : 0;

    return {
      ...r,
      hasConsent: !!c,
      canSeeFuneral,
      canSeeOrgan,
      canSeeContacts,
      gated: !c,
      pct,
      complete: visibleSections > 0 && visibleComplete === visibleSections,
    };
  });

  const total = view.length;
  const activated = view.filter((r) => r.accountActive).length;
  const withConsent = view.filter((r) => r.hasConsent).length;
  const withWishes = view.filter((r) => r.complete).length;
  const inProgress = view.filter((r) => !r.complete && r.pct > 0).length;
  const reviewsDue = view.filter((r) => r.nextReviewDateMs && r.nextReviewDateMs <= Date.now()).length;
  const withFuneral = view.filter((r) => r.canSeeFuneral && r.hasFuneralPlan).length;
  const withOrgan = view.filter((r) => r.canSeeOrgan && r.hasOrganDonation).length;

  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    { key: "residents", label: "Residents" },
    ...(resolvedIsOwner ? [{ key: "staff", label: "Staff" }] : []),
  ];

  const alertBox = (bg, border, color, text) => (
    <div style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 12, padding: "14px 18px", marginBottom: 16,
      color, fontWeight: 600, fontSize: 14,
    }}>{text}</div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: `2px solid ${B.border}`, paddingBottom: 0 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: "10px 20px",
              border: "none",
              background: "none",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              color: activeTab === t.key ? B.orange : B.steel,
              borderBottom: activeTab === t.key ? `3px solid ${B.orange}` : "3px solid transparent",
              marginBottom: -2,
            }}
          >{t.label}</button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", paddingBottom: 6 }}>
          <button
            onClick={load}
            disabled={loading}
            style={{
              padding: "6px 14px", borderRadius: 8, border: `1px solid ${B.border}`,
              background: B.white, color: B.steel, fontWeight: 700, fontSize: 12,
              cursor: loading ? "default" : "pointer",
            }}
          >{loading ? "Refreshing\u2026" : "Refresh"}</button>
        </div>
      </div>

      {err && alertBox("#fef2f2", "#fecaca", "#991b1b", `Could not load residents: ${err}`)}
      {warning && alertBox("#fff7ed", "#fed7aa", "#92400e", warning)}

      {loading ? (
        <div style={{ color: B.steel, padding: 24 }}>Loading resident data&hellip;</div>
      ) : activeTab === "dashboard" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
            <StatCard label="Total Residents" value={total} sub="Linked by referral code" color={B.navy} />
            <StatCard label="Activated Accounts" value={activated} sub={`${total - activated} not yet activated`} color={B.orange} />
            <StatCard label="Consent Recorded" value={withConsent} sub={`${total - withConsent} outstanding`} color={withConsent < total ? B.red : "#065f46"} />
            <StatCard label="Reviews Due" value={reviewsDue} sub="Require attention" color={reviewsDue > 0 ? B.red : "#065f46"} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
            <StatCard label="Wishes Complete" value={withWishes} sub={`${inProgress} in progress`} color="#065f46" />
            <StatCard label="Funeral Preferences" value={withFuneral} sub="Where consent given" color={B.navy} />
            <StatCard label="Organ Donation" value={withOrgan} sub="Where consent given" color={B.navy} />
            <StatCard
              label="Completion Rate"
              value={total > 0 ? `${Math.round((withWishes / total) * 100)}%` : "0%"}
              sub="Of all residents"
              color={B.orange}
            />
          </div>

          {total > 0 && withConsent < total &&
            alertBox("#fef2f2", "#fecaca", "#991b1b",
              `${total - withConsent} resident${total - withConsent > 1 ? "s have" : " has"} no consent recorded. Their wishes data is not visible until consent is recorded.`)}

          {reviewsDue > 0 &&
            alertBox("#fff7ed", "#fed7aa", "#92400e",
              `${reviewsDue} resident${reviewsDue > 1 ? "s have" : " has"} a wishes review due. Go to the Residents tab to action.`)}

          {activated < total &&
            alertBox("#eff6ff", "#bfdbfe", "#1e40af",
              `${total - activated} resident${total - activated > 1 ? "s have" : " has"} not yet activated their LastWishBox account.`)}
        </>
      ) : activeTab === "staff" ? (
        <NursingHomeStaff tenantId={tenantId} isOwner={resolvedIsOwner} />
      ) : (
        <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${B.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ margin: 0, color: B.redDark, fontSize: 18 }}>All Residents</h2>
            <span style={{ fontSize: 13, color: B.steel }}>
              {total} resident{total !== 1 ? "s" : ""}
              {tenant?.refCode ? ` \u00b7 code ${tenant.refCode}` : ""}
            </span>
          </div>

          {view.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: B.steel }}>
              No residents linked to this referral code yet.
            </div>
          ) : (
            <>
              {/* Table - wide screens */}
              <table className="nh-res-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: B.bg }}>
                    {["Resident", "Account", "Consent", "Reviews", ""].map((h, i) => (
                      <th key={i} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: B.redDark, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {view.map((r, i) => (
                    <tr key={r.uid} style={{ borderTop: `1px solid ${B.border}`, background: i % 2 === 0 ? B.white : "#fafafa" }}>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ fontWeight: 700, color: B.redDark }}>{r.name || "\u2014"}</div>
                        <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>{r.email || ""}</div>
                      </td>
                      <td style={{ padding: "12px 16px" }}><StatusBadge active={r.accountActive} /></td>
                      <td style={{ padding: "12px 16px" }}><ConsentBadge consent={r.nhConsent} /></td>
                      <td style={{ padding: "12px 16px", fontSize: 13 }}>
                        {r.nextReviewDateMs ? (
                          <>
                            <div style={{ color: r.nextReviewDateMs <= Date.now() ? B.red : B.navy, fontWeight: 700 }}>
                              Due {fmtDate(r.nextReviewDateMs)}
                            </div>
                            <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>
                              {r.lastReviewDateMs ? `Last ${fmtDate(r.lastReviewDateMs)}` : "No review logged"}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: B.steel }}>No review logged</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <button
                          onClick={() => setOpenResident(r)}
                          style={{
                            padding: "6px 14px", borderRadius: 8, border: "none",
                            background: B.orange, color: B.white,
                            fontWeight: 700, fontSize: 12, cursor: "pointer",
                          }}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Cards - narrow screens */}
              <div className="nh-res-cards">
                {view.map((r) => (
                  <div key={r.uid} className="nh-res-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, color: B.redDark, fontSize: 15 }}>{r.name || "\u2014"}</div>
                        <div style={{ fontSize: 12, color: B.steel, marginTop: 2, wordBreak: "break-all" }}>{r.email || ""}</div>
                      </div>
                      <button
                        onClick={() => setOpenResident(r)}
                        style={{
                          padding: "6px 14px", borderRadius: 8, border: "none",
                          background: B.orange, color: B.white,
                          fontWeight: 700, fontSize: 12, cursor: "pointer", flexShrink: 0,
                        }}>View</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                      <StatusBadge active={r.accountActive} />
                      <ConsentBadge consent={r.nhConsent} />
                    </div>
                    <div style={{ fontSize: 12, color: B.steel, marginTop: 10 }}>
                      {r.nextReviewDateMs ? (
                        <span style={{ color: r.nextReviewDateMs <= Date.now() ? B.red : B.steel, fontWeight: 600 }}>
                          Review due {fmtDate(r.nextReviewDateMs)}
                        </span>
                      ) : "No review logged"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <style>{`
            .nh-res-cards { display: none; }
            @media (max-width: 900px) {
              .nh-res-table { display: none; }
              .nh-res-cards { display: block; padding: 12px; }
              .nh-res-card {
                border: 1px solid ${B.border};
                border-radius: 12px;
                padding: 14px 16px;
                margin-bottom: 10px;
                background: ${B.white};
              }
              .nh-res-card:last-child { margin-bottom: 0; }
            }
          `}</style>
        </div>
      )}

      {openResident && (
        <NursingHomeResidentDrawer
          tenantId={tenantId}
          resident={openResident}
          onClose={() => setOpenResident(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
